import {
  PlaySteadyUnavailableReason,
  isConnectErrorMessage,
  isConnectReadyMessage,
  isConnectResultMessage,
  isSessionErrorMessage,
  isSessionReadyMessage,
  isSessionResultMessage,
} from './messages';
import {
  DEFAULT_COLOR_SCHEME,
  createIframe,
  createOverlay,
  type OverlayColorScheme,
  originFromUrl,
  resolveContainer,
} from './iframe';

/**
 * One entry point for every Sign-in-with-PlaySteady surface. The partner backend
 * mints a token; the browser opens it. `PlaySteady.open()` dispatches on the token
 * prefix — no surface-specific call:
 *
 *   - `ltok_…` → the consent surface (`/embed/connect`). Success =
 *     `onSuccess({ publicToken, userRef })` once the consumer links.
 *   - `dep_secret_…` → the unified deposit surface (`/embed/deposit`).
 *     Success = `onSuccess({ deposit, publicToken?, userRef })`; a first-timer
 *     also links (carrying a `publicToken`). A gate decline fires `onDeclined`
 *     (the deposit was blocked — no money moved).
 *
 * The raw access token never reaches the browser — only the single-use
 * `publicToken`, which the partner exchanges server-to-server.
 *
 * @example
 *   // Deposit: one token, one call.
 *   const { clientSecret } = await playsteady.deposits.create({ amountCents, currency, idempotencyKey });
 *   PlaySteady.open({
 *     token: clientSecret,
 *     onSuccess: ({ deposit, publicToken }) => {
 *       if (publicToken) saveLink(publicToken);   // first-timer linked too
 *       markDepositPending(deposit.id);
 *     },
 *     onDeclined: () => toast('That deposit was declined'),
 *     onCancel: () => toast('Maybe next time'),
 *   });
 */

const DEFAULT_READY_TIMEOUT_MS = 8_000;
/** Production PlaySteady app origin that hosts the embed surfaces. Override via `appUrl`. */
const DEFAULT_APP_URL = 'https://app.playsteady.io';

const DEPOSIT_TOKEN_PREFIX = 'dep_secret_';
const LINK_TOKEN_PREFIX = 'ltok_';

type SurfaceKind = 'connect' | 'session';

export interface PlaySteadyOpenResult {
  /** Present for deposits (`dep_secret_`); always carries the deposit. */
  readonly deposit?: { readonly id: string; readonly status: string };
  /** Present for first-timer linking (every `ltok_`, first-timer `dep_secret_`). */
  readonly publicToken?: string;
  /** The partner's own user ref, echoed from session/link creation. */
  readonly userRef: string | null;
}

export interface PlaySteadyUnavailableInfo {
  readonly reason: PlaySteadyUnavailableReason;
  readonly message: string;
}

export interface PlaySteadyOpenOptions {
  /** The token minted by your backend: `dep_secret_…` (deposit) or `ltok_…` (link). */
  readonly token: string;
  /**
   * Origin hosting the PlaySteady embed surfaces. Defaults to the production app.
   * Set for sandbox / self-hosted deployments.
   */
  readonly appUrl?: string;
  /**
   * Where to render. With `presentation: 'auto'` (default), a container means
   * inline and its absence means a full-screen overlay.
   */
  readonly container?: string | HTMLElement;
  /**
   * - `auto` (default): inline if `container` is set, else an overlay.
   * - `overlay`: always a full-screen modal (ignores `container`).
   * - `inline`: always inline (requires `container`).
   */
  readonly presentation?: 'auto' | 'overlay' | 'inline';
  /** Linked (connect) or deposit approved. */
  readonly onSuccess?: (result: PlaySteadyOpenResult) => void;
  /** Deposit only — declined by the risk/limits gate. */
  readonly onDeclined?: (result: PlaySteadyOpenResult) => void;
  /** Consumer declined consent, cancelled the deposit, or dismissed the overlay. */
  readonly onCancel?: () => void;
  /** Surface loaded + token accepted. Dismiss your spinner. */
  readonly onReady?: () => void;
  /** Surface couldn't complete (bad/expired token, server error, or timeout). */
  readonly onUnavailable?: (info: PlaySteadyUnavailableInfo) => void;
  /** A partner-supplied callback threw. */
  readonly onError?: (error: Error) => void;
  readonly width?: string;
  readonly height?: string;
  /** How long to wait for the `ready` message before `onUnavailable`. Default 8000ms. */
  readonly readyTimeoutMs?: number;
  /**
   * Modal palette: `'light'` (default), `'dark'`, or `'auto'`.
   *
   * Applies to the overlay chrome AND the checkout inside it — they are
   * forwarded the same value so they can't disagree. (Before this was
   * forwarded, the setting reskinned only the backdrop and the checkout stayed
   * dark regardless of what you asked for.)
   *
   * `auto` follows the viewer's OS preference, which is right for a standalone
   * app and wrong for a widget sitting inside your page: under `auto` a light
   * site gets a dark modal whenever the visitor's machine happens to be dark.
   * Pin `'dark'` if your surface has a committed dark palette.
   *
   * An inline mount has no overlay chrome, but the checkout still honours this.
   */
  readonly colorScheme?: OverlayColorScheme;
  /** Auto-close after the result callback returns. Default false. */
  readonly dismissOnResult?: boolean;
}

export interface PlaySteadyHandle {
  /** Dismiss the surface and stop listening. Idempotent. */
  close(): void;
  readonly iframe: HTMLIFrameElement;
}

/** Pick the surface (and its message contract) from the token prefix. */
function surfaceForToken(token: string): SurfaceKind {
  if (token.startsWith(DEPOSIT_TOKEN_PREFIX)) return 'session';
  if (token.startsWith(LINK_TOKEN_PREFIX)) return 'connect';
  throw new Error(
    `@playsteady/sdk-client: open() expects a 'dep_secret_' deposit secret or 'ltok_' link token`,
  );
}

/**
 * `?theme=` for the surface URL, so the checkout renders in the same palette as
 * the chrome around it.
 *
 * `auto` resolves here rather than being passed through, because the iframe
 * can't answer it usefully: it would read the VIEWER's OS preference, while the
 * overlay around it resolved the same word against the same preference at a
 * different moment. Resolving once on this side keeps them identical.
 *
 * Only `dark` is emitted — the surface defaults to light on anything else, so
 * the common case stays a clean URL.
 */
function themeParam(scheme: OverlayColorScheme): string {
  const resolved =
    scheme === 'auto'
      ? globalThis.matchMedia?.('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light'
      : scheme;
  return resolved === 'dark' ? '&theme=dark' : '';
}

/**
 * Build the embed surface URL for a token. Exported for tests / SSR links.
 *
 * `colorScheme` defaults to light, matching {@link DEFAULT_COLOR_SCHEME} — an
 * SSR-rendered link and a runtime `open()` must land on the same palette.
 */
export function openUrl(
  token: string,
  appUrl: string = DEFAULT_APP_URL,
  colorScheme: OverlayColorScheme = DEFAULT_COLOR_SCHEME,
): string {
  const base = appUrl.replace(/\/+$/, '');
  const theme = themeParam(colorScheme);
  return surfaceForToken(token) === 'session'
    ? `${base}/embed/deposit?ds=${encodeURIComponent(token)}${theme}`
    : `${base}/embed/connect?lt=${encodeURIComponent(token)}${theme}`;
}

export function open(opts: PlaySteadyOpenOptions): PlaySteadyHandle {
  if (!opts.token) throw new Error('@playsteady/sdk-client: open() requires a token');
  const surface = surfaceForToken(opts.token);
  const colorScheme = opts.colorScheme ?? DEFAULT_COLOR_SCHEME;
  const url = openUrl(opts.token, opts.appUrl, colorScheme);
  const expectedOrigin = originFromUrl(url);
  const title = surface === 'session' ? 'PlaySteady deposit' : 'Connect with PlaySteady';

  const presentation = opts.presentation ?? 'auto';
  const useInline =
    presentation === 'inline' || (presentation === 'auto' && opts.container !== undefined);
  if (presentation === 'inline' && opts.container === undefined) {
    throw new Error(
      "@playsteady/sdk-client: open() with presentation 'inline' requires a container",
    );
  }

  let closed = false;
  let ready = false;
  let unavailableFired = false;
  let resolved = false;

  let iframe: HTMLIFrameElement;
  let teardown: () => void;
  let revealOverlay: (() => void) | null = null;

  const safe = (fn: (() => void) | undefined): void => {
    try {
      fn?.();
    } catch (err) {
      opts.onError?.(err instanceof Error ? err : new Error(String(err)));
    }
  };

  if (useInline) {
    const container = resolveContainer(opts.container!);
    iframe = createIframe({
      url,
      container,
      title,
      ...(opts.height !== undefined ? { height: opts.height } : {}),
      ...(opts.width !== undefined ? { width: opts.width } : {}),
    });
    teardown = () => iframe.remove();
  } else {
    const overlay = createOverlay({
      url,
      title,
      colorScheme,
      // X / ESC → confirmation; only a confirmed leave lands here, as a cancel.
      onDismiss: () => {
        if (closed) return;
        if (!resolved) {
          resolved = true;
          safe(opts.onCancel);
        }
        handle.close();
      },
    });
    iframe = overlay.iframe;
    teardown = overlay.destroy;
    revealOverlay = overlay.setReady;
  }

  const fireUnavailable = (info: PlaySteadyUnavailableInfo): void => {
    if (unavailableFired) return;
    unavailableFired = true;
    try {
      opts.onUnavailable?.(info);
    } catch (err) {
      opts.onError?.(err instanceof Error ? err : new Error(String(err)));
    }
  };

  const timeoutMs = opts.readyTimeoutMs ?? DEFAULT_READY_TIMEOUT_MS;
  const timer = window.setTimeout(() => {
    if (closed || ready) return;
    fireUnavailable({
      reason: PlaySteadyUnavailableReason.Timeout,
      message: `PlaySteady surface did not report ready within ${timeoutMs}ms`,
    });
  }, timeoutMs);

  const onReadyMessage = (): void => {
    ready = true;
    window.clearTimeout(timer);
    revealOverlay?.();
    safe(opts.onReady);
  };

  const finish = (): void => {
    resolved = true;
    if (opts.dismissOnResult) handle.close();
  };

  const onMessage = (event: MessageEvent): void => {
    if (event.origin !== expectedOrigin) return;
    const data: unknown = event.data;

    if (surface === 'connect') {
      if (isConnectReadyMessage(data)) return onReadyMessage();
      if (isConnectErrorMessage(data)) {
        window.clearTimeout(timer);
        return fireUnavailable({ reason: data.reason, message: data.message });
      }
      if (!isConnectResultMessage(data)) return;
      if (data.status === 'linked') {
        if (!data.public_token) {
          // 'linked' with no token shouldn't happen — surface it rather than
          // calling onSuccess with an empty token.
          resolved = true;
          return fireUnavailable({
            reason: PlaySteadyUnavailableReason.ServerError,
            message: 'Linked result arrived without a public token',
          });
        }
        safe(() =>
          opts.onSuccess?.({ publicToken: data.public_token!, userRef: data.user_ref ?? null }),
        );
      } else {
        safe(opts.onCancel);
      }
      return finish();
    }

    // surface === 'session'
    if (isSessionReadyMessage(data)) return onReadyMessage();
    if (isSessionErrorMessage(data)) {
      window.clearTimeout(timer);
      return fireUnavailable({ reason: data.reason, message: data.message });
    }
    if (!isSessionResultMessage(data)) return;
    const result: PlaySteadyOpenResult = {
      ...(data.deposit ? { deposit: data.deposit } : {}),
      ...(data.public_token ? { publicToken: data.public_token } : {}),
      userRef: data.user_ref ?? null,
    };
    if (data.status === 'succeeded') {
      safe(() => opts.onSuccess?.(result));
    } else if (data.status === 'declined') {
      safe(() => opts.onDeclined?.(result));
    } else {
      safe(opts.onCancel);
    }
    return finish();
  };

  window.addEventListener('message', onMessage);

  const handle: PlaySteadyHandle = {
    close: () => {
      if (closed) return;
      closed = true;
      window.clearTimeout(timer);
      window.removeEventListener('message', onMessage);
      teardown();
    },
    iframe,
  };
  return handle;
}
